import { Adapter, AdapterConnectOptions } from './Adapter';
import { MediaConstraints } from '../index';

const DEFAULT_CONNECT_TIMEOUT = 2000;

export enum WHEPType {
  Client,
  Server
}

export interface WHEPAdapterOptions {
  // Bearer token injected as `Authorization: Bearer <token>` on the SDP
  // POST (offer) and DELETE (teardown) signaling requests. The token is
  // never logged.
  authToken?: string;
  // When set, the WHEP SDP POST (offer) and DELETE (teardown) signaling
  // requests are routed through this caller-supplied proxy instead of being
  // sent directly to the channel URL. The `Location` header returned by the
  // proxied POST is still resolved against the real channel origin so that
  // teardown targets the real resource, not the proxy.
  proxyUrl?: string;
}

export class WHEPAdapter implements Adapter {
  private localPeer: RTCPeerConnection | undefined;
  private channelUrl: URL;
  private authKey?: string;
  private authToken?: string;
  private proxyUrl?: string;
  private debug = false;
  private whepType: WHEPType;
  private waitingForCandidates = false;
  private iceGatheringTimeout: ReturnType<typeof setTimeout> | undefined;
  private resource: string | null = null;
  private onErrorHandler: (error: string) => void;
  private audio: boolean;
  private video: boolean;
  private mediaConstraints: MediaConstraints;
  private closed = false;

  constructor(
    peer: RTCPeerConnection,
    channelUrl: URL,
    onError: (error: string) => void,
    mediaConstraints: MediaConstraints,
    authKey: string | undefined,
    options?: WHEPAdapterOptions
  ) {
    this.mediaConstraints = mediaConstraints;
    this.channelUrl = channelUrl;
    if (typeof this.channelUrl === 'string') {
      throw new Error(
        `channelUrl parameter expected to be an URL not a string`
      );
    }
    this.whepType = WHEPType.Client;
    this.authKey = authKey;
    this.authToken = options?.authToken;
    this.proxyUrl = options?.proxyUrl;

    this.onErrorHandler = onError;
    this.audio = !this.mediaConstraints.videoOnly;
    this.video = !this.mediaConstraints.audioOnly;
    this.resetPeer(peer);
  }

  enableDebug() {
    this.debug = true;
  }

  resetPeer(newPeer: RTCPeerConnection) {
    this.localPeer = newPeer;
    this.localPeer.onicegatheringstatechange =
      this.onIceGatheringStateChange.bind(this);
    this.localPeer.onicecandidate = this.onIceCandidate.bind(this);
  }

  getPeer(): RTCPeerConnection | undefined {
    return this.localPeer;
  }

  // True once teardown has begun, or once the underlying peer connection has
  // been closed (e.g. by `destroy()` on the player before the connection was
  // established). Used to short-circuit any in-flight signaling steps so that
  // operations on a closed peer do not throw `InvalidStateError`.
  private isClosed(): boolean {
    return this.closed || this.localPeer?.signalingState === 'closed';
  }

  async connect(opts?: AdapterConnectOptions) {
    try {
      await this.initSdpExchange(opts?.timeout ?? DEFAULT_CONNECT_TIMEOUT);
    } catch (error) {
      // If teardown happened while the SDP exchange was in flight, the peer is
      // already closed and the resulting error is expected — swallow it quietly
      // instead of reporting a connect error.
      if (this.isClosed()) {
        return;
      }
      console.error((error as Error).toString());
      this.onErrorHandler('connecterror');
    }
  }

  // Builds the Authorization header value for signaling requests. A configured
  // `authToken` is sent as a Bearer credential; otherwise the raw `authKey`
  // header value is used if present. The returned value is applied to request
  // headers only and is never passed to any logging path.
  private getAuthorizationHeader(): string | undefined {
    if (this.authToken) {
      return `Bearer ${this.authToken}`;
    }
    return this.authKey;
  }

  async disconnect() {
    this.closed = true;
    this.waitingForCandidates = false;
    clearTimeout(this.iceGatheringTimeout);
    if (this.resource) {
      this.log(`Disconnecting by removing resource ${this.resource}`);
      const headers: { Authorization?: string } = {};
      const authorization = this.getAuthorizationHeader();
      authorization && (headers['Authorization'] = authorization);
      const response = await fetch(this.resource, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        this.log(`Successfully removed resource`);
      }
    }
  }

  private async initSdpExchange(timeout: number = DEFAULT_CONNECT_TIMEOUT) {
    clearTimeout(this.iceGatheringTimeout);

    if (this.isClosed()) {
      return;
    }

    if (this.localPeer && this.whepType === WHEPType.Client) {
      // Add N recvonly transceivers of each type BEFORE creating the offer so
      // the server can answer with multiple audio/video m-sections. Counts
      // default to a single track each for backward compatibility.
      const numVideoTracks = Math.max(
        1,
        this.mediaConstraints.numVideoTracks ?? 1
      );
      const numAudioTracks = Math.max(
        1,
        this.mediaConstraints.numAudioTracks ?? 1
      );
      if (this.video) {
        for (let i = 0; i < numVideoTracks; i++) {
          this.localPeer.addTransceiver('video', { direction: 'recvonly' });
        }
      }
      if (this.audio) {
        for (let i = 0; i < numAudioTracks; i++) {
          this.localPeer.addTransceiver('audio', { direction: 'recvonly' });
        }
      }
      const offer = await this.localPeer.createOffer();

      // Teardown may have happened while awaiting the offer; bail out before
      // touching the (now closed) peer to avoid an InvalidStateError.
      if (this.isClosed()) {
        return;
      }

      // To add NACK in offer we have to add it manually see https://bugs.chromium.org/p/webrtc/issues/detail?id=4543 for details
      if (offer.sdp) {
        const opusCodecId = offer.sdp.match(/a=rtpmap:(\d+) opus\/48000\/2/);

        if (opusCodecId !== null) {
          offer.sdp = offer.sdp.replace(
            'opus/48000/2\r\n',
            'opus/48000/2\r\na=rtcp-fb:' + opusCodecId[1] + ' nack\r\n'
          );
        }
      }

      await this.localPeer.setLocalDescription(offer);
      this.waitingForCandidates = true;
      this.iceGatheringTimeout = setTimeout(
        this.onIceGatheringTimeout.bind(this),
        timeout
      );
    } else {
      if (this.localPeer) {
        const offer = await this.requestOffer();
        if (this.isClosed()) {
          return;
        }
        await this.localPeer.setRemoteDescription({
          type: 'offer',
          sdp: offer
        });
        const answer = await this.localPeer.createAnswer();
        if (this.isClosed()) {
          return;
        }
        try {
          await this.localPeer.setLocalDescription(answer);
          this.waitingForCandidates = true;
          this.iceGatheringTimeout = setTimeout(
            this.onIceGatheringTimeout.bind(this),
            timeout
          );
        } catch (error) {
          this.log(answer.sdp);
          throw error;
        }
      }
    }
  }

  private async onIceCandidate(event: Event) {
    if (event.type !== 'icecandidate') {
      return;
    }
    const candidateEvent = <RTCPeerConnectionIceEvent>event;
    const candidate: RTCIceCandidate | null = candidateEvent.candidate;
    if (!candidate) {
      return;
    }

    this.log(candidate.candidate);
  }

  private onIceGatheringStateChange(event: Event) {
    if (this.isClosed()) {
      return;
    }
    if (this.localPeer) {
      this.log('IceGatheringState', this.localPeer.iceGatheringState);
      if (
        this.localPeer.iceGatheringState !== 'complete' ||
        !this.waitingForCandidates
      ) {
        return;
      }

      this.onDoneWaitingForCandidates();
    }
  }

  private onIceGatheringTimeout() {
    this.log('IceGatheringTimeout');

    if (this.isClosed() || !this.waitingForCandidates) {
      return;
    }

    this.onDoneWaitingForCandidates();
  }

  private async onDoneWaitingForCandidates() {
    this.waitingForCandidates = false;
    clearTimeout(this.iceGatheringTimeout);

    if (this.isClosed()) {
      return;
    }

    if (this.whepType === WHEPType.Client) {
      await this.sendOffer();
    } else {
      await this.sendAnswer();
    }
  }

  // Endpoint the SDP POST (offer) is sent to. When a proxy is configured the
  // signaling is routed through it; otherwise it goes directly to the channel
  // URL.
  private getSignalingUrl(): string {
    return this.proxyUrl ?? this.channelUrl.toString();
  }

  private getResouceUrlFromHeaders(headers: Headers): string | null {
    const location = headers.get('Location');
    if (!location) {
      return null;
    }
    // A relative Location is resolved against the real channel origin (not the
    // proxy) so that teardown targets the real resource. An absolute Location
    // that a proxy rewrote onto the channel origin is likewise already correct.
    if (location.match(/^\//)) {
      const resourceUrl = new URL(location, this.channelUrl.origin);
      return resourceUrl.toString();
    }
    return location;
  }

  private async requestOffer() {
    if (this.whepType === WHEPType.Server) {
      this.log(`Requesting offer from: ${this.channelUrl}`);
      const headers: { 'Content-Type': string; Authorization?: string } = {
        'Content-Type': 'application/sdp'
      };
      const authorization = this.getAuthorizationHeader();
      authorization && (headers['Authorization'] = authorization);

      const response = await fetch(this.getSignalingUrl(), {
        method: 'POST',
        headers,
        body: ''
      });
      if (response.ok) {
        this.resource = this.getResouceUrlFromHeaders(response.headers);
        this.log('WHEP Resource', this.resource);
        const offer = await response.text();
        this.log('Received offer', offer);
        return offer;
      } else {
        const serverMessage = await response.text();
        throw new Error(serverMessage);
      }
    }
  }

  private async sendAnswer() {
    if (!this.localPeer) {
      this.log('Local RTC peer not initialized');
      return;
    }

    if (this.isClosed()) {
      return;
    }

    if (this.whepType === WHEPType.Server && this.resource) {
      const answer = this.localPeer.localDescription;
      if (answer) {
        const headers: { 'Content-Type': string; Authorization?: string } = {
          'Content-Type': 'application/sdp'
        };
        const authorization = this.getAuthorizationHeader();
        authorization && (headers['Authorization'] = authorization);
        const response = await fetch(this.resource, {
          method: 'PATCH',
          headers,
          body: answer.sdp
        });
        if (!response.ok) {
          this.error(`sendAnswer response: ${response.status}`);
        }
      }
    }
  }

  private async sendOffer() {
    if (!this.localPeer) {
      this.log('Local RTC peer not initialized');
      return;
    }

    if (this.isClosed()) {
      return;
    }

    const offer = this.localPeer.localDescription;

    if (this.whepType === WHEPType.Client && offer) {
      this.log(`Sending offer to ${this.channelUrl}`);
      const headers: { 'Content-Type': string; Authorization?: string } = {
        'Content-Type': 'application/sdp'
      };
      const authorization = this.getAuthorizationHeader();
      authorization && (headers['Authorization'] = authorization);
      const response = await fetch(this.getSignalingUrl(), {
        method: 'POST',
        headers,
        body: offer.sdp
      });

      if (response.ok) {
        this.resource = this.getResouceUrlFromHeaders(response.headers);
        this.log('WHEP Resource', this.resource);
        const answer = await response.text();
        // Teardown may have happened while awaiting the answer body; do not
        // apply the remote description to a closed peer.
        if (this.isClosed()) {
          return;
        }
        await this.localPeer.setRemoteDescription({
          type: 'answer',
          sdp: answer
        });
      } else if (response.status === 400) {
        this.log(`server does not support client-offer, need to reconnect`);
        this.whepType = WHEPType.Server;
        this.onErrorHandler('reconnectneeded');
      } else if (
        response.status === 406 &&
        this.audio &&
        !this.mediaConstraints.audioOnly &&
        !this.mediaConstraints.videoOnly
      ) {
        this.log(
          `maybe server does not support audio. Let's retry without audio`
        );
        this.audio = false;
        this.video = true;
        this.onErrorHandler('reconnectneeded');
      } else if (
        response.status === 406 &&
        this.video &&
        !this.mediaConstraints.audioOnly &&
        !this.mediaConstraints.videoOnly
      ) {
        this.log(
          `maybe server does not support video. Let's retry without video`
        );
        this.audio = true;
        this.video = false;
        this.onErrorHandler('reconnectneeded');
      } else {
        this.error(`sendAnswer response: ${response.status}`);
        this.onErrorHandler('connectionfailed');
      }
    }
  }

  private log(...args: any[]) {
    if (this.debug) {
      console.log('WebRTC-player', ...args);
    }
  }

  private error(...args: any[]) {
    console.error('WebRTC-player', ...args);
  }
}
